import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { startWith, map, takeUntil, finalize } from 'rxjs/operators';
import { Markets } from '@models/markets';
import { SinalizationService } from '@services/sinalization.service';
import { Store, select } from '@ngrx/store';
import { AppState } from '@store/state/app.state';
import { getMarkets } from '@store/selectors/market.selector';
import { GetSinalization } from '@store/actions/user.action';
import { MessageService } from '@services/message.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-nova-sinalizacao',
    templateUrl: './nova-sinalizacao.component.html',
    styleUrls: ['./nova-sinalizacao.component.scss'],
    standalone: false
})
export class NovaSinalizacaoComponent implements OnInit, OnDestroy {

  signalingForm: UntypedFormGroup;
  sent = false;
  submitted = false;
  pending = false;

  percentageValues = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
  public markets: Markets[] = [];
  selectedMarket: string;

  filteredOptions: Observable<Markets[]>;

  private readonly unsubscribe$: Subject<void> = new Subject();

  constructor(private readonly formBuilder: UntypedFormBuilder,
              private readonly sinalizationService: SinalizationService,
              private readonly messageService: MessageService,
              private readonly router: Router,
              public store: Store<AppState>,
              private readonly translateService: TranslateService) {

    this.store.pipe(
      select(getMarkets),
      takeUntil(this.unsubscribe$)
    ).subscribe(markets => {
        this.markets = markets.filter((market) => market.sc === 'OMIP');
    });
  }

  private _filter(name: string): Markets[] {
    const filterValue = name.toLowerCase();

    return this.markets.filter(option => option.ns.toLowerCase().includes(filterValue));
  }

  displayFn(market: Markets): string {
    return market && market.ns ? market.ns.split('_').join(' ') : '';
  }

  ngOnInit() {
    this.signalingForm = this.formBuilder.group({
      productType: ['', Validators.required],
      percValue: ['', Validators.required]
    });
    this.filteredOptions = this.signalingForm.get('productType').valueChanges
      .pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.ns),
        map(name => name ? this._filter(name) : this.markets.slice())
      );
  }

  get f() { return this.signalingForm.controls; }

  onSubmit() {
    if (this.signalingForm.invalid) {
        return;
    }
    this.selectedMarket = this.signalingForm.value.productType.ns.split('_').join(' ');
    this.sent = true;
  }

  confirm(): void {

    let currentValue: number;
    let maturity = '';

    for (const market of this.markets) {
      if (market.ns === this.signalingForm.value.productType.ns) {
        currentValue = market.vl;
        if (market.mt !== '') {
          maturity = market.mt;
        }
      }
    }

    const mtv: number = currentValue + ((this.signalingForm.value.percValue / 100) * currentValue);

    let args: any;

    if (maturity !== '') {
      args = {
        mns: this.signalingForm.value.productType.ns,
        mtv,
        mmt: maturity,
        mcv: currentValue,
        mpv: this.signalingForm.value.percValue
      };
    } else {
      args = {
        mns: this.signalingForm.value.productType.ns,
        mtv,
        mcv: currentValue,
        mpv: this.signalingForm.value.percValue
      };
    }

    this.pending = true;
    this.sinalizationService.createSinalization(args).pipe(
      finalize(() => this.pending = false)
    ).subscribe((response) => {
      if (response.status === 200 && response.success) {
        this.store.dispatch(new GetSinalization());
        this.messageService.show(this.translateService.instant('edp.emarket.fast.quote.sinalizacao.enviada'));
        this.router.navigate(['/signalings']);
      } else {
        this.messageService.show(this.translateService.instant('edp.emarket.fast.quote.erro.enviar'), 'error');
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
